import './_HttpStatusErrors.scss'

import React, { useEffect, useState } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import { Box, Button, CssBaseline, Divider, Grid, Typography } from '@mui/material'

import * as paths from '../../../../utils/Links'
import * as variable from '../../../../utils/Variable'
import LayoutWrapper from '../../layout/LayoutWrapper'
import PhoneIcon from '../../../../assets/images/icons/phone.svg'
import EmailIcon from '../../../../assets/images/icons/email.svg'

import { getErrorDetails } from './utils/getErrorDetails'
import { getRedirectInfo } from './utils/getRedirectInfo'

import { LocationState } from './types/IHttpStatusError'

const HttpStatusErrors = (): JSX.Element => {
  const oktaAuth = useOktaAuth()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { statusCode } = useParams<{ statusCode: string }>()

  const isAuthenticated: boolean = oktaAuth?.authState?.isAuthenticated ?? false
  const errorPageState = (state || {}) as LocationState
  const statusCodeNumber: number = statusCode && !isNaN(parseInt(statusCode, 10)) ? parseInt(statusCode, 10) : 0

  const isRedirectEnabled: boolean =
    errorPageState?.isRedirectEnabled !== undefined ? errorPageState.isRedirectEnabled : false
  const redirectCountdown: number = errorPageState?.redirectCountdown || 60 // Default of 60 seconds.

  const { title: redirectPageTitle, path: redirectPath } = getRedirectInfo(isAuthenticated, errorPageState)

  const [countdown, setCountdown] = useState<number>(redirectCountdown)
  const [redirecting, setRedirecting] = useState<boolean>(false)

  useEffect(() => {
    // Check if the status code is within the valid HTTP error range (400-599)
    if (statusCodeNumber < 400 || statusCodeNumber > 599) {
      navigate(paths.getNotFound())
    }
  }, [statusCodeNumber, navigate])

  useEffect(() => {
    if (isRedirectEnabled) {
      if (countdown <= 0) return

      const timer = setTimeout(() => {
        if (countdown === 1) {
          setRedirecting(true)
          navigate(redirectPath, { replace: true })
        } else {
          setCountdown(countdown - 1)
        }
      }, 1000)

      // Clean up the timer when component unmounts or countdown changes.
      return () => clearTimeout(timer)
    }
  }, [countdown, isAuthenticated, isRedirectEnabled, redirectPath, navigate])

  const { alertLevel, title, heading } = getErrorDetails(statusCodeNumber, errorPageState)

  return (
    <div className={`api-response-${alertLevel}`}>
      <LayoutWrapper>
        <CssBaseline />
        <Grid container className="api-response-container" data-test="api-response-container">
          <Grid item xs={12}>
            <Box mb={6} className="Card-shadow">
              <div className="Header-content" data-test="Header-content">
                {alertLevel === 'error' && (
                  <span
                    className="lw-system-icons-close icons-fs-48 icons-bg-colorCrimsonLight icons-colorErrorDark icons-bg-round icons-font-bold"
                    data-test="checkmark-icon"
                  />
                )}
                {alertLevel === 'warning' && (
                  <span
                    className="lw-system-icons-alert icons-fs-48 icons-bg-colorAmberLight icons-colorAmberDark icons-bg-round icons-font-bold"
                    data-test="checkmark-icon"
                  />
                )}
                <Typography variant="h1" data-test="Header-title">
                  {title}
                </Typography>
                <Typography variant="body1" data-test="Header-text">
                  {heading}
                </Typography>
              </div>
              <Divider className="solid-divider" />
              <div className="content">
                <div className="text-center">
                  If you have further issues, please don't hesitate to contact our team.
                  <br />
                  {isRedirectEnabled && (
                    <span className="countdown-text" data-test="countdown-text">
                      {redirecting ? (
                        <>
                          Redirecting to the <strong>{redirectPageTitle}</strong> page...
                        </>
                      ) : (
                        <>
                          You will be redirected to the <strong>{redirectPageTitle}</strong> page in{' '}
                          <strong>{countdown}</strong> seconds.
                        </>
                      )}
                    </span>
                  )}
                </div>
                <div className="cta-btn text-center" style={{ margin: '10px 0' }}>
                  <Button
                    className="continueButton"
                    variant="contained"
                    color="primary"
                    onClick={() => navigate(redirectPath, { replace: true })}
                  >
                    {redirectPageTitle}
                  </Button>
                </div>
              </div>
              <div className="contact-info-wrapper">
                <div className="contact-info" data-test="customer-service-number">
                  <img src={PhoneIcon} alt="" data-test="phone-icon" />
                  <a
                    href={`tel:${variable.customerServiceNumberCall}`}
                    title="Fluro contact number"
                    data-test="customer-service-number-cta"
                  >
                    {variable.customerServiceNumber}
                  </a>
                </div>
                <div className="contact-info" data-test="customer-service-email">
                  <img src={EmailIcon} alt="" data-test="email-icon" />
                  <a
                    href={`mailto:${variable.customerServiceEmail}`}
                    title="Fluro contact email"
                    data-test="customer-service-email-cta"
                  >
                    {variable.customerServiceEmail}
                  </a>
                </div>
                <div className="contact-times" data-test="customer-service-hours">
                  <Typography variant="body2">{variable.openingHours}</Typography>
                </div>
              </div>
            </Box>
          </Grid>
        </Grid>
      </LayoutWrapper>
    </div>
  )
}

export default HttpStatusErrors
